<template>
    <app-layout title="Settings">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Settings - Printers</template>

                <template #actions>
                    <jet-button type="button" class="h-7 leading-none" @click="openAddModal">
                        Add Printer
                    </jet-button>
                </template>
            </page-header>

            <settings-navigation />

            <div class="flex px-8">
                <div class="flex gap-x-2 w-3/4 items-end">
                    <div class="flex flex-col flex-1">
                        <jet-input type="text" class="h-8" name="search" v-model="mainForm.search" @input="handleSubmit" placeholder="Search..."/>
                    </div>
                    <div class="flex flex-col w-40">
                        <multiselect
                            v-model="mainForm.division"
                            :options="divisionOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="bottom"
                            @change="handleSubmit"
                        />
                    </div>
                    <div class="flex flex-col w-40">
                        <multiselect
                            v-model="mainForm.status"
                            :options="statusOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="bottom"
                            @change="handleSubmit"
                        />
                    </div>
                    <div class="self-end">
                        <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset">
                            Reset
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>

            <div class="px-8 mt-2">
                <div class="rounded-t overflow-hidden border border-b-0 w-full">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="name">Name</sortable-header>
                                <th class="text-left">Divisions</th>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="queue_name">Queue Name</sortable-header>
                                <th class="text-left">Initial Labels</th>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="exclusive">Exclusive</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="active">Status</sortable-header>
                                <th class="text-left">Claimed By</th>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="pending_count">Pending</sortable-header>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="printer in printers.data" :key="printer.id" class="cursor-pointer" @click="editPrinter(printer)">
                                <td class="text-left" @click.stop>
                                    <Link :href="route('settings.printers.show', printer.id)" class="text-blue-600 hover:underline">
                                        {{ printer.name }}
                                    </Link>
                                </td>
                                <td class="text-left">{{ printer.divisions_label }}</td>
                                <td class="text-left">{{ printer.queue_name }}</td>
                                <td class="text-left">{{ printer.initial_labels_label }}</td>
                                <td class="text-left">{{ printer.exclusive ? 'Yes' : '' }}</td>
                                <td class="text-left">{{ printer.active ? 'Active' : 'Inactive' }}</td>
                                <td class="text-left">{{ (printer.claimed_by_names || []).join(', ') }}</td>
                                <td class="text-left">{{ printer.pending_count || '' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4 w-full">
                    <pagination class="mt-0 mb-0" :links="printers.links" />

                    <div class="w-40">
                        <multiselect
                            v-model.number="mainForm.paginate"
                            :options="paginateOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="top"
                            @change="handleSubmit"
                        />
                    </div>
                </div>
            </div>

            <printer-form-modal
                :show="showPrinterModal"
                :mode="showPrinterModal"
                :form="form"
                :initial-by-division="initialByDivision"
                :active-counts-by-division="activeCountsByDivision"
                :show-delete-modal="showDeleteModal"
                @close="closePrinterModal"
                @submit="submitForm"
                @delete-confirm="showDeleteModal = true"
                @delete-cancel="showDeleteModal = false"
                @delete="handleDeletePrinter"
            />
        </form>
    </app-layout>
</template>

<script>
import { useForm, Link } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetInput from '@/Jetstream/Input';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import PrinterFormModal from '@/Pages/Settings/Partials/PrinterFormModal';
import Multiselect from '@vueform/multiselect';
import Pagination from '@/Jetstream/Pagination';
import SortableHeader from '@/Shared/SortableHeader';

import '@vueform/multiselect/themes/default.css';

export default {
    setup(props) {
        const mainForm = useForm({
            paginate: props.paginate,
            search: props.search,
            division: props.division || '',
            status: props.status || 'all',
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
        });

        const form = useForm({
            id: null,
            name: '',
            queue_name: '',
            model: '',
            ip_address: '',
            label_width: '',
            label_height: '',
            unprintable_margin: '',
            divisions: [],
            initial_label_divisions: [],
            exclusive: false,
            is_mobile: false,
            active: true,
        });

        return { mainForm, form };
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetTertiaryButton,
        JetInput,
        SettingsNavigation,
        PrinterFormModal,
        Multiselect,
        Pagination,
        SortableHeader,
        Link,
    },

    props: [
        'printers',
        'search',
        'division',
        'status',
        'sortColumn',
        'sortDirection',
        'paginate',
        'initialByDivision',
        'activeCountsByDivision',
    ],

    data() {
        return {
            showPrinterModal: false,
            showDeleteModal: false,
            divisionOptions: [
                { value: '', label: 'All divisions' },
                { value: 'wholesale', label: 'Wholesale' },
                { value: 'schools', label: 'Schools' },
                { value: 'retail', label: 'Retail' },
            ],
            statusOptions: [
                { value: 'active', label: 'Active Only' },
                { value: 'all', label: 'All statuses' },
            ],
            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
        };
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.mainForm.get(route('settings.printers.index'), { preserveState: true });
        }, 250),

        openAddModal() {
            this.form.reset();
            this.form.clearErrors();
            this.form.active = true;
            this.form.divisions = [];
            this.form.initial_label_divisions = [];
            this.showPrinterModal = 'add';
        },

        editPrinter(data) {
            this.form.id = data.id;
            this.form.name = data.name;
            this.form.queue_name = data.queue_name;
            this.form.model = data.model || '';
            this.form.ip_address = data.ip_address || '';
            this.form.label_width = data.label_width ?? '';
            this.form.label_height = data.label_height ?? '';
            this.form.unprintable_margin = data.unprintable_margin ?? '';
            this.form.divisions = [...(data.division_codes || [])];
            this.form.initial_label_divisions = [...(data.initial_label_divisions || [])];
            this.form.exclusive = !!data.exclusive;
            this.form.is_mobile = !!data.is_mobile;
            this.form.active = !!data.active;
            this.form.clearErrors();
            this.showPrinterModal = 'edit';
        },

        handleReset() {
            this.mainForm.search = '';
            this.mainForm.division = '';
            this.mainForm.status = 'all';
            this.mainForm.sortColumn = 'name';
            this.mainForm.sortDirection = 'asc';
            this.handleSubmit();
        },

        handleSort(sort) {
            this.mainForm.sortColumn = sort.column;
            this.mainForm.sortDirection = sort.direction;
            this.handleSubmit();
        },

        closePrinterModal() {
            this.form.reset();
            this.form.clearErrors();
            this.showPrinterModal = false;
            this.showDeleteModal = false;
        },

        submitForm() {
            const payload = {
                ...this.form.data(),
                label_width: this.form.label_width === '' ? null : this.form.label_width,
                label_height: this.form.label_height === '' ? null : this.form.label_height,
                unprintable_margin: this.form.unprintable_margin === '' ? null : this.form.unprintable_margin,
                model: this.form.model || null,
                ip_address: this.form.ip_address || null,
                divisions: this.form.divisions || [],
                initial_label_divisions: this.form.initial_label_divisions || [],
            };

            if (this.showPrinterModal === 'add') {
                this.form.transform(() => payload).post(this.route('settings.printers.store'), {
                    onSuccess: () => this.closePrinterModal(),
                });
                return;
            }

            this.form.transform(() => payload).put(this.route('settings.printers.update', this.form.id), {
                onSuccess: () => this.closePrinterModal(),
            });
        },

        handleDeletePrinter() {
            this.form.delete(this.route('settings.printers.destroy', this.form.id), {
                onSuccess: () => this.closePrinterModal(),
                onError: () => { this.showDeleteModal = false; },
            });
        },
    },
};
</script>

<style scoped>
</style>
